import { Component, signal, inject, DOCUMENT, OnInit, effect } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap, of, from, tap, distinctUntilChanged } from 'rxjs';
import { ThemeService } from './@core/services/theme.service';
import { MediaUploadService } from './@core/services/media-upload.service';
import { OrganizationContextService } from './@core/services/organization-context.service';
import { OrganizationService } from './@core/services/organization.service';
import { PwaUpdateService } from './@core/services/pwa-update.service';
import { Toast } from 'primeng/toast';
import { LoggerService } from './@core/services/logger.service';

@Component({
  selector: 'root',
  imports: [RouterOutlet, Toast],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected readonly title = signal('qwentro');
  protected readonly themeService = inject(ThemeService);
  private document = inject(DOCUMENT);
  private mediaUploadService = inject(MediaUploadService);
  private organizationContext = inject(OrganizationContextService);
  private organizationService = inject(OrganizationService);
  protected pwaUpdate = inject(PwaUpdateService);
  private logger = inject(LoggerService);

  constructor() {
    this.pwaUpdate.initialize();

    // Apply theme class to html element for proper dark mode support
    effect(() => {
      const theme = this.themeService.currentTheme();
      const htmlElement = this.document.documentElement;
      // Remove any existing theme classes
      htmlElement.classList.remove('light', 'dark');
      // Add the current theme class
      htmlElement.classList.add(theme);
    });

    // Load organization data and apply brand colors reactively.
    // Uses toObservable + switchMap for reliable signal→async reactivity
    // (same proven pattern used in organization-settings component).
    toObservable(this.organizationContext.effectiveOrganizationId)
      .pipe(
        distinctUntilChanged(),
        switchMap((orgId) => {
          if (orgId) {
            return from(this.organizationService.getById(orgId));
          }
          return of(null);
        }),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: (org) => {
          if (org) {
            this.themeService.applyOrganizationColors({
              primary: org.primary_color,
              success: org.success_color,
              warning: org.warning_color,
              danger: org.danger_color,
              info: org.info_color,
            });
          } else {
            this.themeService.resetToDefaultTheme();
          }
        },
        error: (err) => {
          this.logger.error('Failed to load organization for theming', err instanceof Error ? err : undefined);
        },
      });
  }

  ngOnInit(): void {
    // Signal the splash screen to dismiss now that Angular is ready
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('app-ready'));
    }
  }
}
